import { Component, Inject, ViewChild } from "@angular/core";
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogRef,
} from "@angular/material/dialog";
import { TranslateService } from "@ngx-translate/core";
import { UploaderAcceptFormat } from "../../../../../shared/components/file-uploader/file-uploader.component";
import { LinkedInVideoUploadMaxFileSize } from "../../../../../shared/defaults/file-uploader-max-file-size";
import { ImageEntity } from "../../../../../shared/entities/file/image.entity";
import {
  LinkedInPostContentType,
  PostPlatform,
} from "../../../../../shared/enums/campaignPost.enums";
import { ByteUnit } from "../../../../../shared/functions/byte-unit";
import { BrandCampaignAddContentDialogData } from "../../../../../shared/models/brand-campaign-add-content-dialog-data";
import { BrandCampaignLinkedInPost } from "../../../../../shared/models/brandCampaignLinkedInPost";
import { AppDataService } from "../../../../../shared/services/api/app-data.service";
import { DialogService } from "../../../../../shared/services/dialog.service";
import { NotificationService } from "../../../../../shared/services/notification.service";
import { BrandCampaignContentFormPostSuggestedTextComponent } from "../../../brand-campaign-content/brand-campaign-content-form-post-text/brand-campaign-content-form-post-suggested-text.component";
import { BrandCampaignPostService } from "../../../shared/services/brand-campaign-post.service";
import {
  LinkedinPostCreationBaseDialogComponent,
  LinkedInPostUploadData,
} from "../linkedin-post-creation-base-dialog.component";
import { AnyToVideoEntityMapper } from "../../../../../shared/mappers/video.entity.mapper";

export interface LinkedinVideoPostCreationDialogData
  extends BrandCampaignAddContentDialogData {
  readonly contentType?: LinkedInPostContentType;
  readonly post?: BrandCampaignLinkedInPost;
}

export type LinkedinVideoPostCreationDialogResult = undefined;

@Component({
  selector: "app-linkedin-video-post-creation-dialog",
  templateUrl: "./linkedin-video-post-creation-dialog.component.html",
  styleUrls: [
    "./linkedin-video-post-creation-dialog.component.scss",
    "../../campaign-content-creation-dialog.scss",
  ],
})
export class LinkedinVideoPostCreationDialogComponent extends LinkedinPostCreationBaseDialogComponent {
  public readonly MAX_FILE_SIZE = LinkedInVideoUploadMaxFileSize;
  public readonly uploadData: LinkedInPostUploadData;
  public readonly fileUploaderResponseMapper = AnyToVideoEntityMapper;
  public uploaderUrl: string;
  public videoUploaderSubtitle: string;
  public acceptVideoFormat = UploaderAcceptFormat.VideoMP4;
  public uploaderFieldName = "file";
  protected postType = LinkedInPostContentType.Video;

  @ViewChild("suggestedText")
  public suggestedText?: BrandCampaignContentFormPostSuggestedTextComponent;

  constructor(
    protected readonly translateService: TranslateService,
    protected readonly brandCampaignPostService: BrandCampaignPostService,
    protected readonly notificationService: NotificationService,
    protected readonly dialogService: DialogService,
    protected readonly dialogRef: MatDialogRef<
      LinkedinVideoPostCreationDialogComponent,
      LinkedinVideoPostCreationDialogResult
    >,
    protected readonly dialog: MatDialog,
    protected readonly appDataService: AppDataService,
    @Inject(MAT_DIALOG_DATA) data: LinkedinVideoPostCreationDialogData,
  ) {
    super(
      brandCampaignPostService,
      dialogRef,
      dialogService,
      notificationService,
      translateService,
      dialog,
      appDataService,
      data,
    );

    this.uploaderUrl =
      this.brandCampaignPostService.getAddCampaignDetailCategoryPostFileUrl(
        this.campaign.id,
        this.campaignDetails.id,
        this.category.id,
      );

    this.uploadData = {
      media_type: LinkedInPostContentType.Video,
      platform: PostPlatform.LinkedIn,
    };

    if (this.post?.id) {
      this.uploadData.post_id = this.post.id.toString();
    }

    this.videoUploaderSubtitle =
      this.translateService.instant(
        "brand.campaign.contentCalendarVideoPost.validFileExtension",
      ) +
      " mp4. Max " +
      ByteUnit.convert(this.MAX_FILE_SIZE, ByteUnit.Byte, ByteUnit.MB) +
      " MB";
  }

  public textChanged(text: string): void {
    this.post.text = text;
    this.checkSaveDisabled();
  }

  public checkSaveDisabled(): void {
    if (!this.post.videoUrl || !this.post.text || this.isSaving) {
      this.isSaveDisabled = true;
      return;
    }

    if (this.suggestedText?.isValid === false) {
      this.isSaveDisabled = true;
      return;
    }

    super.checkSaveDisabled();
  }

  public handleVideoUploadResponse(apiResponse: ImageEntity): void {
    this.post.videoFileId = apiResponse.id;
    this.post.videoUrl = apiResponse.videoUrl;
    this.checkSaveDisabled();
    this.notificationService.success("shared.videoUploadedSuccessfully");
  }
}
